import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Grid,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Skeleton,
  Tooltip,
  LinearProgress,
  Snackbar,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  Refresh,
  ArrowBack,
  Business,
  Person,
  Add,
  Flag,
  Warning,
  ReportProblem,
  ChangeCircle,
  Description,
  OpenInNew,
  AttachMoney,
  AccountBalanceWallet,
  ShoppingCart,
  Receipt,
  Payments,
  TrendingUp,
  TrendingDown,
  Assessment,
  ListAlt,
  CalendarMonth,
  Engineering,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TypePill,
  ErrorState,
  TableEmptyState,
  StatusTone,
} from '../../components/common/pageKit';
import { formatCurrency, formatIndianNumber, formatIndianDate } from '../../utils/currency';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
} from 'recharts';
import projectAnalyticsService from '../../services/projectAnalyticsService';
import projectExtrasService, {
  MILESTONE_STATUSES,
  RISK_LEVELS,
  ISSUE_SEVERITIES,
} from '../../services/projectExtrasService';

// ---------------------------------------------------------------------------
// Project Workspace — the flagship 360 for a single project. Replaces a plain
// edit form with a command-center: hero header (identity + health story),
// a financial/EVM summary band, and progressive-disclosure tabs for every
// governance surface (plan, tasks, milestones, costs, billing, procurement,
// risks, issues, change requests, documents, timeline).
//
// The /project-analytics/project/:id/360 payload shape is loosely typed on
// the backend ("may vary") so every nested read below goes through optional
// chaining with an explicit fallback — a missing key must degrade to an
// empty state, never a crash.
// ---------------------------------------------------------------------------

const safeArr = <T,>(v: any): T[] => (Array.isArray(v) ? v : []);

const numOrNull = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const moneyCompact = (v: any): React.ReactNode => {
  const n = numOrNull(v);
  return n === null ? null : formatIndianNumber(n);
};

const moneyTitle = (v: any): string | undefined => {
  const n = numOrNull(v);
  return n === null ? undefined : formatCurrency(n);
};

const ratioText = (v: any): string => {
  const n = numOrNull(v);
  return n === null ? '—' : n.toFixed(2);
};

const dateText = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : formatIndianDate(d);
};

const isOverdue = (dueDate: any, status: any): boolean => {
  if (!dueDate) return false;
  const s = String(status || '').toUpperCase();
  if (['DONE', 'COMPLETED', 'CLOSED', 'CANCELLED'].includes(s)) return false;
  const d = new Date(dueDate);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
};

const healthTone = (band: any): StatusTone => {
  const b = String(band || '').toUpperCase();
  if (b === 'ON_TRACK') return 'success';
  if (b === 'WATCH') return 'info';
  if (b === 'AT_RISK') return 'warning';
  if (b === 'CRITICAL') return 'error';
  return 'neutral';
};

const signalTone = (state: any): StatusTone => {
  const s = String(state || '').toUpperCase();
  if (['GOOD', 'OK', 'POSITIVE', 'HEALTHY'].includes(s)) return 'success';
  if (['WATCH', 'INFO'].includes(s)) return 'info';
  if (['WARNING', 'WARN', 'CAUTION'].includes(s)) return 'warning';
  if (['BAD', 'CRITICAL', 'NEGATIVE', 'BREACH'].includes(s)) return 'error';
  return 'neutral';
};

const milestoneTone = (status: any): StatusTone => {
  const s = String(status || '').toUpperCase();
  if (s === 'COMPLETED') return 'success';
  if (s === 'IN_PROGRESS') return 'info';
  if (s === 'AT_RISK') return 'warning';
  if (s === 'DELAYED') return 'error';
  return 'neutral';
};

const taskTone = (status: any): StatusTone => {
  const s = String(status || '').toUpperCase();
  if (['DONE', 'COMPLETED'].includes(s)) return 'success';
  if (['IN_PROGRESS', 'ACTIVE'].includes(s)) return 'info';
  if (['BLOCKED', 'ON_HOLD'].includes(s)) return 'warning';
  if (['CANCELLED'].includes(s)) return 'neutral';
  return 'neutral';
};

const riskTone = (status: any): StatusTone => {
  const s = String(status || '').toUpperCase();
  if (s === 'CLOSED') return 'success';
  if (s === 'MITIGATING') return 'info';
  if (s === 'ACCEPTED') return 'neutral';
  if (s === 'OPEN') return 'warning';
  return 'neutral';
};

const riskLevelTone = (level: any): StatusTone => {
  const s = String(level || '').toUpperCase();
  if (s === 'LOW') return 'success';
  if (s === 'MEDIUM') return 'warning';
  if (s === 'HIGH') return 'error';
  return 'neutral';
};

const issueTone = (status: any): StatusTone => {
  const s = String(status || '').toUpperCase();
  if (s === 'RESOLVED' || s === 'CLOSED') return 'success';
  if (s === 'IN_PROGRESS') return 'info';
  if (s === 'OPEN') return 'warning';
  return 'neutral';
};

const severityTone = (sev: any): StatusTone => {
  const s = String(sev || '').toUpperCase();
  if (s === 'LOW') return 'success';
  if (s === 'MEDIUM') return 'info';
  if (s === 'HIGH') return 'warning';
  if (s === 'CRITICAL') return 'error';
  return 'neutral';
};

const crTone = (status: any): StatusTone => {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'IMPLEMENTED') return 'success';
  if (s === 'SUBMITTED' || s === 'UNDER_REVIEW') return 'info';
  if (s === 'REJECTED') return 'error';
  return 'neutral';
};

const billingTone = (status: any): StatusTone => {
  const s = String(status || '').toUpperCase();
  if (s === 'COLLECTED') return 'success';
  if (s === 'BILLED') return 'info';
  if (s === 'READY') return 'warning';
  if (s === 'CANCELLED') return 'error';
  return 'neutral';
};

const TAB_KEYS = [
  'overview',
  'plan',
  'tasks',
  'milestones',
  'costs',
  'billing',
  'procurement',
  'risks',
  'issues',
  'changeRequests',
  'documents',
  'timeline',
] as const;
type TabKey = typeof TAB_KEYS[number];

const TAB_LABELS: Record<TabKey, string> = {
  overview: 'Overview',
  plan: 'Plan',
  tasks: 'Tasks',
  milestones: 'Milestones',
  costs: 'Costs',
  billing: 'Billing',
  procurement: 'Procurement',
  risks: 'Risks',
  issues: 'Issues',
  changeRequests: 'Change Requests',
  documents: 'Documents',
  timeline: 'Timeline',
};

const RISK_ORDER = ['LOW', 'MEDIUM', 'HIGH'];

interface ToastState {
  message: string;
  severity: 'success' | 'error';
}

const emptyMilestoneForm = () => ({ name: '', targetDate: '', isBillingTrigger: false, billingAmount: '' });
const emptyRiskForm = () => ({ title: '', probability: 'MEDIUM', impact: 'MEDIUM', mitigation: '' });
const emptyIssueForm = () => ({ title: '', severity: 'MEDIUM', owner: '' });
const emptyCrForm = () => ({ title: '', costImpact: '', scheduleImpactDays: '', description: '' });
const emptyDocForm = () => ({ fileName: '', url: '' });

const ProjectWorkspacePage: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ct = useChartTokens();

  const id = idParam ? parseInt(idParam, 10) : NaN;

  const [p360, setP360] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [costData, setCostData] = useState<any>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);

  const [procData, setProcData] = useState<any>(null);
  const [procLoading, setProcLoading] = useState(false);
  const [procError, setProcError] = useState<string | null>(null);

  const [billData, setBillData] = useState<any>(null);
  const [billLoading, setBillLoading] = useState(false);
  const [billError, setBillError] = useState<string | null>(null);

  const [tab, setTab] = useState<TabKey>('overview');
  const [toast, setToast] = useState<ToastState | null>(null);

  const [milestoneOpen, setMilestoneOpen] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState(emptyMilestoneForm());
  const [riskOpen, setRiskOpen] = useState(false);
  const [riskForm, setRiskForm] = useState(emptyRiskForm());
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState(emptyIssueForm());
  const [crOpen, setCrOpen] = useState(false);
  const [crForm, setCrForm] = useState(emptyCrForm());
  const [docOpen, setDocOpen] = useState(false);
  const [docForm, setDocForm] = useState(emptyDocForm());
  const [saving, setSaving] = useState(false);

  const fetchP360 = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError('Invalid project — no id in the URL.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await projectAnalyticsService.project360(id);
      setP360(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load this project workspace');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchCosts = useCallback(async () => {
    if (!Number.isFinite(id)) return;
    setCostLoading(true);
    setCostError(null);
    try {
      const data = await projectAnalyticsService.costLedger(id);
      setCostData(data);
    } catch (err: any) {
      setCostError(err.message || 'Failed to load the cost ledger');
    } finally {
      setCostLoading(false);
    }
  }, [id]);

  const fetchProcurement = useCallback(async () => {
    if (!Number.isFinite(id)) return;
    setProcLoading(true);
    setProcError(null);
    try {
      const data = await projectAnalyticsService.procurement(id);
      setProcData(data);
    } catch (err: any) {
      setProcError(err.message || 'Failed to load procurement');
    } finally {
      setProcLoading(false);
    }
  }, [id]);

  const fetchBilling = useCallback(async () => {
    if (!Number.isFinite(id)) return;
    setBillLoading(true);
    setBillError(null);
    try {
      const data = await projectAnalyticsService.billing(id);
      setBillData(data);
    } catch (err: any) {
      setBillError(err.message || 'Failed to load billing');
    } finally {
      setBillLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchP360();
    fetchCosts();
    fetchProcurement();
    fetchBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const header = p360?.header || {};
  const financials = p360?.financials || {};
  const _fc: any = financials?.forecast || {};
  // The API emits EVM figures lower-cased (eac/etc/vac/cpi/spi/costVariance/
  // scheduleVariance); normalize to the upper-cased keys the tiles read.
  const forecast: any = {
    EAC: _fc.EAC ?? _fc.eac,
    ETC: _fc.ETC ?? _fc.etc,
    VAC: _fc.VAC ?? _fc.vac,
    CV: _fc.CV ?? _fc.costVariance,
    SV: _fc.SV ?? _fc.scheduleVariance,
    CPI: _fc.CPI ?? _fc.cpi,
    SPI: _fc.SPI ?? _fc.spi,
  };
  const health = p360?.health || { band: null, score: null, signals: [] };

  const phases = safeArr<any>(p360?.phases);
  const milestones = safeArr<any>(p360?.milestones);
  const risks = safeArr<any>(p360?.risks);
  const issues = safeArr<any>(p360?.issues);
  const changeRequests = safeArr<any>(p360?.changeRequests);
  const tasks = safeArr<any>(p360?.tasks);
  const billingMilestones = safeArr<any>(p360?.billingMilestones);
  const documents = safeArr<any>(p360?.documents);
  const activityTimeline = safeArr<any>(p360?.activityTimeline);

  const goTab = (t: TabKey) => setTab(t);

  const notifyAndReload = async (message: string) => {
    setToast({ message, severity: 'success' });
    await fetchP360();
  };

  const handleSaveMilestone = async () => {
    if (!Number.isFinite(id) || !milestoneForm.name.trim()) return;
    setSaving(true);
    try {
      await projectExtrasService.createMilestone(id, {
        name: milestoneForm.name.trim(),
        targetDate: milestoneForm.targetDate || undefined,
        isBillingTrigger: milestoneForm.isBillingTrigger,
        billingAmount: milestoneForm.isBillingTrigger && milestoneForm.billingAmount
          ? Number(milestoneForm.billingAmount)
          : undefined,
      });
      setMilestoneOpen(false);
      setMilestoneForm(emptyMilestoneForm());
      await notifyAndReload('Milestone added');
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to add milestone', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleMilestoneStatus = async (milestoneId: number, status: string) => {
    try {
      await projectExtrasService.setMilestoneStatus(milestoneId, status);
      await notifyAndReload('Milestone status updated');
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update milestone', severity: 'error' });
    }
  };

  const handleSaveRisk = async () => {
    if (!Number.isFinite(id) || !riskForm.title.trim()) return;
    setSaving(true);
    try {
      await projectExtrasService.createRisk(id, {
        title: riskForm.title.trim(),
        probability: riskForm.probability,
        impact: riskForm.impact,
        mitigation: riskForm.mitigation || undefined,
      });
      setRiskOpen(false);
      setRiskForm(emptyRiskForm());
      await notifyAndReload('Risk logged');
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to log risk', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIssue = async () => {
    if (!Number.isFinite(id) || !issueForm.title.trim()) return;
    setSaving(true);
    try {
      await projectExtrasService.createIssue(id, {
        title: issueForm.title.trim(),
        severity: issueForm.severity,
        owner: issueForm.owner || undefined,
      });
      setIssueOpen(false);
      setIssueForm(emptyIssueForm());
      await notifyAndReload('Issue raised');
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to raise issue', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCr = async () => {
    if (!Number.isFinite(id) || !crForm.title.trim()) return;
    setSaving(true);
    try {
      await projectExtrasService.createChangeRequest(id, {
        title: crForm.title.trim(),
        costImpact: crForm.costImpact ? Number(crForm.costImpact) : undefined,
        scheduleImpactDays: crForm.scheduleImpactDays ? Number(crForm.scheduleImpactDays) : undefined,
        description: crForm.description || undefined,
      });
      setCrOpen(false);
      setCrForm(emptyCrForm());
      await notifyAndReload('Change request created');
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to create change request', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCrStatus = async (crId: number, status: string) => {
    try {
      await projectExtrasService.setChangeRequestStatus(crId, status);
      await notifyAndReload(`Change request ${status.toLowerCase()}`);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update change request', severity: 'error' });
    }
  };

  const handleBillingMilestoneStatus = async (bmId: number, status: string) => {
    try {
      await projectExtrasService.setBillingMilestoneStatus(bmId, status);
      setToast({ message: 'Billing milestone updated', severity: 'success' });
      await Promise.all([fetchP360(), fetchBilling()]);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update billing milestone', severity: 'error' });
    }
  };

  const handleSaveDocument = async () => {
    if (!Number.isFinite(id) || !docForm.fileName.trim() || !docForm.url.trim()) return;
    setSaving(true);
    try {
      await projectExtrasService.createDocument(id, {
        fileName: docForm.fileName.trim(),
        url: docForm.url.trim(),
      });
      setDocOpen(false);
      setDocForm(emptyDocForm());
      await notifyAndReload('Document link added');
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to add document', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Budget-burn mini chart data: phases (budget vs actual) fall back to a
  // single project-level bar when phase breakdown is absent.
  const burnChartData = useMemo(() => {
    if (phases.length > 0) {
      return phases.map((ph: any, i: number) => ({
        name: ph?.name || ph?.phase_name || `Phase ${i + 1}`,
        budget: numOrNull(ph?.budget) ?? 0,
        actual: numOrNull(ph?.actual ?? ph?.actual_cost) ?? 0,
      }));
    }
    const b = numOrNull(financials?.budget);
    const a = numOrNull(financials?.actualCost);
    if (b === null && a === null) return [];
    return [{ name: header?.project_name || 'Project', budget: b ?? 0, actual: a ?? 0 }];
  }, [phases, financials, header]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={320} height={44} />
        <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={200} sx={{ mb: 2.5, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={110} sx={{ mb: 2.5, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error || !p360) {
    return (
      <Box>
        <PageHeader
          title="Project Workspace"
          actions={
            <IconButton onClick={() => navigate('/projects')} aria-label="Back to projects">
              <ArrowBack />
            </IconButton>
          }
        />
        <ErrorState message={error || undefined} onRetry={fetchP360} />
      </Box>
    );
  }

  const statusTone: StatusTone =
    String(header?.status || '').toUpperCase() === 'COMPLETED'
      ? 'success'
      : String(header?.status || '').toUpperCase() === 'ON_HOLD'
      ? 'warning'
      : String(header?.status || '').toUpperCase() === 'CANCELLED'
      ? 'error'
      : 'info';

  return (
    <Box>
      <PageHeader
        title={header?.project_name || 'Project Workspace'}
        qualifier={header?.project_code ? `— ${header.project_code}` : undefined}
        subtitle={header?.project_type || header?.priority ? [header?.project_type, header?.priority].filter(Boolean).join(' · ') : undefined}
        actions={
          <>
            <Tooltip title="Back to Projects">
              <IconButton onClick={() => navigate('/projects')} aria-label="Back to projects">
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={() => { fetchP360(); fetchCosts(); fetchProcurement(); fetchBilling(); }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button size="small" variant="outlined" startIcon={<Flag />} onClick={() => setMilestoneOpen(true)}>
              Add Milestone
            </Button>
            <Button size="small" variant="outlined" startIcon={<Warning />} onClick={() => setRiskOpen(true)}>
              Add Risk
            </Button>
            <Button size="small" variant="outlined" startIcon={<ReportProblem />} onClick={() => setIssueOpen(true)}>
              Add Issue
            </Button>
            <Button size="small" variant="contained" startIcon={<ChangeCircle />} onClick={() => setCrOpen(true)}>
              Create Change Request
            </Button>
          </>
        }
      />

      {/* Hero header */}
      <Paper elevation={0} sx={{ p: 3, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <StatusDot label={header?.status || 'Unknown'} tone={statusTone} />
              {header?.priority && <TypePill label={header.priority} tint="#7c3aed" />}
            </Stack>

            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
              <Business sx={{ fontSize: 18 }} />
              <Typography sx={{ fontWeight: 700 }}>{header?.customer_name || 'No customer linked'}</Typography>
            </Box>

            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Project Manager</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Person sx={{ fontSize: 15, color: 'text.disabled' }} />
                  {header?.project_manager_name || '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Start Date</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{dateText(header?.start_date)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Planned End</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{dateText(header?.planned_end_date || header?.end_date)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Actual/Target End</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{dateText(header?.end_date)}</Typography>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Progress</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {numOrNull(header?.progress_percent) === null ? '—' : `${numOrNull(header?.progress_percent)}%`}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, numOrNull(header?.progress_percent) ?? 0))}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
                Project Health
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ mt: 0.5, mb: 1.25 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '2rem', fontVariantNumeric: 'tabular-nums' }}>
                  {numOrNull(health?.score) === null ? '—' : health.score}
                </Typography>
                <StatusDot label={health?.band || 'Unknown'} tone={healthTone(health?.band)} />
              </Stack>
              {safeArr<any>(health?.signals).length > 0 ? (
                <Stack spacing={0.75}>
                  {safeArr<any>(health.signals).map((s, i) => (
                    <Box key={i}>
                      <StatusDot label={s?.dimension || 'Signal'} tone={signalTone(s?.state)} />
                      {s?.detail && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1.75 }}>
                          {s.detail}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.disabled">No health signals recorded.</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Financial summary band */}
      <Grid container spacing={2} sx={{ mb: 1.5 }}>
        <Grid size={{ xs: 6, sm: 4, md: 4 / 3 }}>
          <StatCard title="Contract Value" value={moneyCompact(financials?.contractValue)} icon={<AttachMoney />} color="#0284c7" onClick={() => goTab('billing')} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 / 3 }}>
          <StatCard title="Budget" value={moneyCompact(financials?.budget)} icon={<AccountBalanceWallet />} color="#6366f1" onClick={() => goTab('costs')} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 / 3 }}>
          <StatCard title="Actual Cost" value={moneyCompact(financials?.actualCost)} icon={<TrendingUp />} color="#ea580c" onClick={() => goTab('costs')} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 / 3 }}>
          <StatCard title="Committed" value={moneyCompact(financials?.committedCost)} icon={<ShoppingCart />} color="#7c3aed" onClick={() => goTab('procurement')} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 / 3 }}>
          <StatCard title="Forecast (EAC)" value={moneyCompact(forecast?.EAC)} icon={<Assessment />} color="#d97706" onClick={() => goTab('costs')} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 / 3 }}>
          <StatCard title="Gross Margin" value={moneyCompact(financials?.grossMargin)} icon={<TrendingDown />} color="#059669" onClick={() => goTab('costs')} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 / 3 }}>
          <StatCard title="Billed" value={moneyCompact(financials?.billed)} icon={<Receipt />} color="#0891b2" onClick={() => goTab('billing')} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 / 3 }}>
          <StatCard title="Collected" value={moneyCompact(financials?.collected)} icon={<Payments />} color="#16a34a" onClick={() => goTab('billing')} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 / 3 }}>
          <StatCard title="Unbilled" value={moneyCompact(financials?.unbilled)} icon={<ListAlt />} color="#dc2626" onClick={() => goTab('billing')} />
        </Grid>
      </Grid>

      {/* EVM strip */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em', display: 'block', mb: 1 }}>
          Earned Value
        </Typography>
        <Grid container spacing={2}>
          {[
            { label: 'EAC', value: moneyCompact(forecast?.EAC), title: moneyTitle(forecast?.EAC) },
            { label: 'ETC', value: moneyCompact(forecast?.ETC), title: moneyTitle(forecast?.ETC) },
            { label: 'VAC', value: moneyCompact(forecast?.VAC), title: moneyTitle(forecast?.VAC) },
            { label: 'CV', value: moneyCompact(forecast?.CV), title: moneyTitle(forecast?.CV) },
            { label: 'SV', value: moneyCompact(forecast?.SV), title: moneyTitle(forecast?.SV) },
            { label: 'CPI', value: ratioText(forecast?.CPI), title: undefined },
            { label: 'SPI', value: ratioText(forecast?.SPI), title: undefined },
          ].map((m) => (
            <Grid key={m.label} size={{ xs: 4, sm: 3, md: 12 / 7 }} sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">{m.label}</Typography>
              <Typography
                title={m.title}
                sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {m.value === null || m.value === undefined ? '—' : m.value}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          {TAB_KEYS.map((k) => (
            <Tab key={k} value={k} label={TAB_LABELS[k]} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }} />
          ))}
        </Tabs>

        <Box sx={{ p: 2.5 }}>
          {tab === 'overview' && (
            <OverviewTab
              health={health}
              burnChartData={burnChartData}
              ct={ct}
              tasks={tasks}
              milestones={milestones}
              risks={risks}
              issues={issues}
            />
          )}

          {tab === 'plan' && <PhasesTab phases={phases} />}

          {tab === 'tasks' && <TasksTab tasks={tasks} />}

          {tab === 'milestones' && (
            <MilestonesTab
              milestones={milestones}
              onAdd={() => setMilestoneOpen(true)}
              onStatusChange={handleMilestoneStatus}
            />
          )}

          {tab === 'costs' && (
            <CostsTab data={costData} loading={costLoading} error={costError} onRetry={fetchCosts} />
          )}

          {tab === 'billing' && (
            <BillingTab
              data={billData}
              loading={billLoading}
              error={billError}
              onRetry={fetchBilling}
              billingMilestones={billingMilestones}
              onMarkBilled={handleBillingMilestoneStatus}
            />
          )}

          {tab === 'procurement' && (
            <ProcurementTab data={procData} loading={procLoading} error={procError} onRetry={fetchProcurement} />
          )}

          {tab === 'risks' && (
            <RisksTab risks={risks} onAdd={() => setRiskOpen(true)} />
          )}

          {tab === 'issues' && (
            <IssuesTab issues={issues} onAdd={() => setIssueOpen(true)} />
          )}

          {tab === 'changeRequests' && (
            <ChangeRequestsTab
              changeRequests={changeRequests}
              onAdd={() => setCrOpen(true)}
              onStatus={handleCrStatus}
            />
          )}

          {tab === 'documents' && (
            <DocumentsTab documents={documents} onAdd={() => setDocOpen(true)} />
          )}

          {tab === 'timeline' && <TimelineTab items={activityTimeline} ct={ct} navigate={navigate} />}
        </Box>
      </Paper>

      {/* Add milestone dialog */}
      <Dialog open={milestoneOpen} onClose={() => setMilestoneOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Milestone</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              required
              value={milestoneForm.name}
              onChange={(e) => setMilestoneForm((f) => ({ ...f, name: e.target.value }))}
            />
            <TextField
              fullWidth
              type="date"
              label="Target Date"
              InputLabelProps={{ shrink: true }}
              value={milestoneForm.targetDate}
              onChange={(e) => setMilestoneForm((f) => ({ ...f, targetDate: e.target.value }))}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={milestoneForm.isBillingTrigger}
                  onChange={(e) => setMilestoneForm((f) => ({ ...f, isBillingTrigger: e.target.checked }))}
                />
              }
              label="Is a billing trigger"
            />
            {milestoneForm.isBillingTrigger && (
              <TextField
                fullWidth
                type="number"
                label="Billing Amount"
                value={milestoneForm.billingAmount}
                onChange={(e) => setMilestoneForm((f) => ({ ...f, billingAmount: e.target.value }))}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setMilestoneOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMilestone} disabled={saving || !milestoneForm.name.trim()}>
            {saving ? 'Saving…' : 'Add Milestone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add risk dialog */}
      <Dialog open={riskOpen} onClose={() => setRiskOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Risk</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              required
              value={riskForm.title}
              onChange={(e) => setRiskForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              select
              fullWidth
              label="Probability"
              value={riskForm.probability}
              onChange={(e) => setRiskForm((f) => ({ ...f, probability: e.target.value }))}
            >
              {RISK_LEVELS.map((l) => (
                <MenuItem key={l} value={l}>{l}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Impact"
              value={riskForm.impact}
              onChange={(e) => setRiskForm((f) => ({ ...f, impact: e.target.value }))}
            >
              {RISK_LEVELS.map((l) => (
                <MenuItem key={l} value={l}>{l}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Mitigation"
              value={riskForm.mitigation}
              onChange={(e) => setRiskForm((f) => ({ ...f, mitigation: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setRiskOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRisk} disabled={saving || !riskForm.title.trim()}>
            {saving ? 'Saving…' : 'Add Risk'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add issue dialog */}
      <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Issue</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              required
              value={issueForm.title}
              onChange={(e) => setIssueForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              select
              fullWidth
              label="Severity"
              value={issueForm.severity}
              onChange={(e) => setIssueForm((f) => ({ ...f, severity: e.target.value }))}
            >
              {ISSUE_SEVERITIES.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Owner"
              value={issueForm.owner}
              onChange={(e) => setIssueForm((f) => ({ ...f, owner: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setIssueOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveIssue} disabled={saving || !issueForm.title.trim()}>
            {saving ? 'Saving…' : 'Add Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create change request dialog */}
      <Dialog open={crOpen} onClose={() => setCrOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Change Request</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              required
              value={crForm.title}
              onChange={(e) => setCrForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              fullWidth
              type="number"
              label="Cost Impact"
              value={crForm.costImpact}
              onChange={(e) => setCrForm((f) => ({ ...f, costImpact: e.target.value }))}
            />
            <TextField
              fullWidth
              type="number"
              label="Schedule Impact (days)"
              value={crForm.scheduleImpactDays}
              onChange={(e) => setCrForm((f) => ({ ...f, scheduleImpactDays: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description"
              value={crForm.description}
              onChange={(e) => setCrForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCrOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCr} disabled={saving || !crForm.title.trim()}>
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add document dialog */}
      <Dialog open={docOpen} onClose={() => setDocOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Document Link</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="File Name"
              required
              value={docForm.fileName}
              onChange={(e) => setDocForm((f) => ({ ...f, fileName: e.target.value }))}
            />
            <TextField
              fullWidth
              label="URL"
              required
              value={docForm.url}
              onChange={(e) => setDocForm((f) => ({ ...f, url: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDocOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveDocument}
            disabled={saving || !docForm.fileName.trim() || !docForm.url.trim()}
          >
            {saving ? 'Saving…' : 'Add Document'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

const OverviewTab: React.FC<{
  health: any;
  burnChartData: Array<{ name: string; budget: number; actual: number }>;
  ct: ReturnType<typeof useChartTokens>;
  tasks: any[];
  milestones: any[];
  risks: any[];
  issues: any[];
}> = ({ health, burnChartData, ct, tasks, milestones, risks, issues }) => {
  const openTasks = tasks.filter((t) => !['DONE', 'COMPLETED'].includes(String(t?.status || '').toUpperCase())).length;
  const upcomingMilestones = milestones.filter((m) => String(m?.status || '').toUpperCase() !== 'COMPLETED').length;
  const openRisks = risks.filter((r) => String(r?.status || '').toUpperCase() !== 'CLOSED').length;
  const openIssues = issues.filter((i) => !['RESOLVED', 'CLOSED'].includes(String(i?.status || '').toUpperCase())).length;

  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard title="Open Tasks" value={openTasks} icon={<ListAlt />} color="#0284c7" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard title="Pending Milestones" value={upcomingMilestones} icon={<Flag />} color="#7c3aed" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard title="Open Risks" value={openRisks} icon={<Warning />} color="#d97706" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard title="Open Issues" value={openIssues} icon={<ReportProblem />} color="#dc2626" />
        </Grid>
      </Grid>

      {burnChartData.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Budget vs Actual</Typography>
          <Box sx={{ height: 280, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={burnChartData} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
                <CartesianGrid {...gridProps(ct)} />
                <XAxis dataKey="name" {...axisProps(ct)} />
                <YAxis {...axisProps(ct)} width={64} tickFormatter={(v) => formatIndianNumber(Number(v))} />
                <RTooltip {...tooltipStyle(ct)} formatter={(v: any, n: any) => [formatCurrency(Number(v)), n]} />
                <Bar dataKey="budget" name="Budget" fill={ct.series(0)} radius={[3, 3, 0, 0]} maxBarSize={36} />
                <Bar dataKey="actual" name="Actual" fill={ct.series(1)} radius={[3, 3, 0, 0]} maxBarSize={36} />
                <Line type="monotone" dataKey="actual" name="Actual trend" stroke={ct.series(1)} strokeWidth={2} dot={false} legendType="none" />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Health Signals</Typography>
        {safeArr<any>(health?.signals).length === 0 ? (
          <TableEmptyState title="No health signals recorded" />
        ) : (
          <Stack spacing={1}>
            {safeArr<any>(health.signals).map((s, i) => (
              <Paper key={i} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{s?.dimension || 'Signal'}</Typography>
                  <StatusDot label={s?.state || '—'} tone={signalTone(s?.state)} />
                </Stack>
                {s?.detail && (
                  <Typography variant="caption" color="text.secondary">{s.detail}</Typography>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Plan (phases) tab
// ---------------------------------------------------------------------------

const PhasesTab: React.FC<{ phases: any[] }> = ({ phases }) => {
  if (phases.length === 0) return <TableEmptyState title="No phases defined" hint="Phase breakdown will appear here once the plan is set up." />;
  return (
    <TableSurface>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Phase</TableCell>
              <TableCell sx={headerCellSx} align="right">Budget</TableCell>
              <TableCell sx={headerCellSx} align="right">Actual</TableCell>
              <TableCell sx={headerCellSx}>Progress</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {phases.map((ph, i) => (
              <TableRow key={ph?.id ?? i} sx={bodyRowSx}>
                <TableCell sx={{ fontWeight: 600 }}>{ph?.name || ph?.phase_name || `Phase ${i + 1}`}</TableCell>
                <TableCell align="right" title={moneyTitle(ph?.budget)}>{moneyCompact(ph?.budget) ?? '—'}</TableCell>
                <TableCell align="right" title={moneyTitle(ph?.actual ?? ph?.actual_cost)}>{moneyCompact(ph?.actual ?? ph?.actual_cost) ?? '—'}</TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.max(0, numOrNull(ph?.progress ?? ph?.progress_percent) ?? 0))}
                      sx={{ flex: 1, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption">{numOrNull(ph?.progress ?? ph?.progress_percent) ?? '—'}%</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <TypePill label={ph?.status || '—'} tint="#64748b" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TableSurface>
  );
};

// ---------------------------------------------------------------------------
// Tasks tab
// ---------------------------------------------------------------------------

const TasksTab: React.FC<{ tasks: any[] }> = ({ tasks }) => {
  if (tasks.length === 0) return <TableEmptyState title="No tasks yet" hint="Tasks assigned to this project will appear here." />;
  return (
    <TableSurface>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Title</TableCell>
              <TableCell sx={headerCellSx}>Assignee</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell sx={headerCellSx}>Priority</TableCell>
              <TableCell sx={headerCellSx}>Due</TableCell>
              <TableCell sx={headerCellSx}>Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((t, i) => {
              const overdue = isOverdue(t?.due_date, t?.status);
              return (
                <TableRow key={t?.id ?? i} sx={{ ...bodyRowSx, bgcolor: overdue ? 'rgba(220, 38, 38, 0.06)' : undefined }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t?.title || t?.task_name || '—'}</TableCell>
                  <TableCell>{t?.assignee_name || t?.assignee || '—'}</TableCell>
                  <TableCell><StatusDot label={t?.status || '—'} tone={taskTone(t?.status)} /></TableCell>
                  <TableCell><TypePill label={t?.priority || '—'} tint="#7c3aed" /></TableCell>
                  <TableCell sx={{ color: overdue ? 'error.main' : undefined, fontWeight: overdue ? 700 : 400 }}>
                    {dateText(t?.due_date)}{overdue ? ' (overdue)' : ''}
                  </TableCell>
                  <TableCell>{numOrNull(t?.progress ?? t?.progress_percent) ?? '—'}{numOrNull(t?.progress ?? t?.progress_percent) !== null ? '%' : ''}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </TableSurface>
  );
};

// ---------------------------------------------------------------------------
// Milestones tab
// ---------------------------------------------------------------------------

const MilestonesTab: React.FC<{
  milestones: any[];
  onAdd: () => void;
  onStatusChange: (id: number, status: string) => void;
}> = ({ milestones, onAdd, onStatusChange }) => (
  <Box>
    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
      <Button size="small" startIcon={<Add />} onClick={onAdd}>Add Milestone</Button>
    </Stack>
    {milestones.length === 0 ? (
      <TableEmptyState title="No milestones yet" hint="Add a milestone to start tracking delivery checkpoints." />
    ) : (
      <TableSurface>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Name</TableCell>
                <TableCell sx={headerCellSx}>Target Date</TableCell>
                <TableCell sx={headerCellSx}>Billing Trigger</TableCell>
                <TableCell sx={headerCellSx} align="right">Billing Amount</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Update Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {milestones.map((m, i) => {
                const highlight = ['AT_RISK', 'DELAYED'].includes(String(m?.status || '').toUpperCase());
                return (
                  <TableRow key={m?.id ?? i} sx={{ ...bodyRowSx, bgcolor: highlight ? 'rgba(217, 119, 6, 0.06)' : undefined }}>
                    <TableCell sx={{ fontWeight: 600 }}>{m?.name || '—'}</TableCell>
                    <TableCell>{dateText(m?.target_date)}</TableCell>
                    <TableCell>{m?.is_billing_trigger ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right" title={moneyTitle(m?.billing_amount)}>{moneyCompact(m?.billing_amount) ?? '—'}</TableCell>
                    <TableCell><StatusDot label={m?.status || '—'} tone={milestoneTone(m?.status)} /></TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value=""
                        displayEmpty
                        onChange={(e: SelectChangeEvent) => e.target.value && m?.id && onStatusChange(m.id, e.target.value)}
                        sx={{ minWidth: 140, fontSize: '0.8125rem' }}
                      >
                        <MenuItem value="" disabled>Change status…</MenuItem>
                        {MILESTONE_STATUSES.filter((s) => s !== m?.status).map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Costs tab
// ---------------------------------------------------------------------------

const CostsTab: React.FC<{ data: any; loading: boolean; error: string | null; onRetry: () => void }> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  if (loading) return <Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  const rows = safeArr<any>(data?.ledger ?? data?.rows ?? data?.entries ?? data);
  const committed = safeArr<any>(data?.committed);

  let running = 0;

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Cost Ledger</Typography>
        {rows.length === 0 ? (
          <TableEmptyState title="No cost entries yet" hint="Costs posted against this project will appear here." />
        ) : (
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Date</TableCell>
                    <TableCell sx={headerCellSx}>Type</TableCell>
                    <TableCell sx={headerCellSx}>Source</TableCell>
                    <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                    <TableCell sx={headerCellSx} align="right">Running Total</TableCell>
                    <TableCell sx={headerCellSx}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => {
                    const amt = numOrNull(r?.amount) ?? 0;
                    running += amt;
                    return (
                      <TableRow key={r?.id ?? i} sx={bodyRowSx}>
                        <TableCell>{dateText(r?.date ?? r?.posted_date ?? r?.created_at)}</TableCell>
                        <TableCell><TypePill label={r?.type ?? r?.cost_type ?? '—'} tint="#0284c7" /></TableCell>
                        <TableCell>{r?.source ?? r?.reference ?? '—'}</TableCell>
                        <TableCell align="right" title={moneyTitle(r?.amount)}>{moneyCompact(r?.amount) ?? '—'}</TableCell>
                        <TableCell align="right" title={formatCurrency(running)}>{formatIndianNumber(running)}</TableCell>
                        <TableCell align="right">
                          {r?.link && (
                            <Tooltip title="Open source document">
                              <IconButton size="small" component="a" href={r.link} target="_blank" rel="noopener noreferrer">
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        )}
      </Box>

      {committed.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Committed</Typography>
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Reference</TableCell>
                    <TableCell sx={headerCellSx}>Source</TableCell>
                    <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {committed.map((c, i) => (
                    <TableRow key={c?.id ?? i} sx={bodyRowSx}>
                      <TableCell>{c?.reference ?? c?.doc_number ?? '—'}</TableCell>
                      <TableCell>{c?.source ?? '—'}</TableCell>
                      <TableCell align="right" title={moneyTitle(c?.amount)}>{moneyCompact(c?.amount) ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Billing tab
// ---------------------------------------------------------------------------

const BillingTab: React.FC<{
  data: any;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  billingMilestones: any[];
  onMarkBilled: (id: number, status: string) => void;
}> = ({ data, loading, error, onRetry, billingMilestones, onMarkBilled }) => {
  const position = data?.position ?? data;

  return (
    <Stack spacing={2.5}>
      {loading ? (
        <Skeleton variant="rounded" height={100} sx={{ borderRadius: 3 }} />
      ) : error ? (
        <ErrorState message={error} onRetry={onRetry} />
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard title="Contract Value" value={moneyCompact(position?.contractValue)} icon={<AttachMoney />} color="#0284c7" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard title="Billed" value={moneyCompact(position?.billed)} icon={<Receipt />} color="#0891b2" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard title="Collected" value={moneyCompact(position?.collected)} icon={<Payments />} color="#16a34a" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard title="Unbilled" value={moneyCompact(position?.unbilled)} icon={<ListAlt />} color="#dc2626" />
          </Grid>
        </Grid>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Billing Milestones</Typography>
        {billingMilestones.length === 0 ? (
          <TableEmptyState title="No billing milestones" hint="Milestone-based billing checkpoints will appear here." />
        ) : (
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Name</TableCell>
                    <TableCell sx={headerCellSx} align="right">%</TableCell>
                    <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingMilestones.map((bm, i) => {
                    const status = String(bm?.status || '').toUpperCase();
                    const canBill = ['PENDING', 'READY'].includes(status);
                    return (
                      <TableRow key={bm?.id ?? i} sx={bodyRowSx}>
                        <TableCell sx={{ fontWeight: 600 }}>{bm?.name || '—'}</TableCell>
                        <TableCell align="right">{numOrNull(bm?.percent ?? bm?.percentage) ?? '—'}{numOrNull(bm?.percent ?? bm?.percentage) !== null ? '%' : ''}</TableCell>
                        <TableCell align="right" title={moneyTitle(bm?.amount)}>{moneyCompact(bm?.amount) ?? '—'}</TableCell>
                        <TableCell><StatusDot label={bm?.status || '—'} tone={billingTone(bm?.status)} /></TableCell>
                        <TableCell align="right">
                          {canBill && bm?.id && (
                            <Button size="small" variant="outlined" onClick={() => onMarkBilled(bm.id, 'BILLED')}>
                              Mark Billed
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        )}
      </Box>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Procurement tab
// ---------------------------------------------------------------------------

const ProcurementTab: React.FC<{ data: any; loading: boolean; error: string | null; onRetry: () => void }> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  if (loading) return <Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  const pos = safeArr<any>(data?.purchaseOrders ?? data?.pos ?? data);
  if (pos.length === 0) return <TableEmptyState title="No purchase orders linked" hint="POs raised against this project will appear here." />;

  return (
    <TableSurface>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>PO Number</TableCell>
              <TableCell sx={headerCellSx}>Vendor</TableCell>
              <TableCell sx={headerCellSx} align="right">Ordered</TableCell>
              <TableCell sx={headerCellSx} align="right">Received</TableCell>
              <TableCell sx={headerCellSx} align="right">Billed</TableCell>
              <TableCell sx={headerCellSx} align="right">Paid</TableCell>
              <TableCell sx={headerCellSx} align="right">Committed</TableCell>
              <TableCell sx={headerCellSx} align="right">Actual</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pos.map((po, i) => (
              <TableRow key={po?.id ?? i} sx={bodyRowSx}>
                <TableCell sx={{ fontWeight: 600 }}>{po?.po_number ?? po?.number ?? '—'}</TableCell>
                <TableCell>{po?.vendor_name ?? po?.vendor ?? '—'}</TableCell>
                <TableCell align="right" title={moneyTitle(po?.ordered)}>{moneyCompact(po?.ordered) ?? '—'}</TableCell>
                <TableCell align="right" title={moneyTitle(po?.received)}>{moneyCompact(po?.received) ?? '—'}</TableCell>
                <TableCell align="right" title={moneyTitle(po?.billed)}>{moneyCompact(po?.billed) ?? '—'}</TableCell>
                <TableCell align="right" title={moneyTitle(po?.paid)}>{moneyCompact(po?.paid) ?? '—'}</TableCell>
                <TableCell align="right" title={moneyTitle(po?.committed)}>{moneyCompact(po?.committed) ?? '—'}</TableCell>
                <TableCell align="right" title={moneyTitle(po?.actual)}>{moneyCompact(po?.actual) ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TableSurface>
  );
};

// ---------------------------------------------------------------------------
// Risks tab (table + matrix)
// ---------------------------------------------------------------------------

const RisksTab: React.FC<{ risks: any[]; onAdd: () => void }> = ({ risks, onAdd }) => {
  const matrix = useMemo(() => {
    const grid: number[][] = RISK_ORDER.map(() => RISK_ORDER.map(() => 0));
    risks.forEach((r) => {
      const p = RISK_ORDER.indexOf(String(r?.probability || '').toUpperCase());
      const im = RISK_ORDER.indexOf(String(r?.impact || '').toUpperCase());
      if (p >= 0 && im >= 0) grid[p][im] += 1;
    });
    return grid;
  }, [risks]);

  const cellColor = (probIdx: number, impIdx: number): string => {
    const score = probIdx + impIdx;
    if (score <= 1) return '#0ca30c33';
    if (score === 2) return '#fab21933';
    if (score === 3) return '#ec835a33';
    return '#d03b3b33';
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Risk Matrix</Typography>
          <Button size="small" startIcon={<Add />} onClick={onAdd}>Add Risk</Button>
        </Stack>
        <Box sx={{ display: 'inline-block' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: `80px repeat(${RISK_ORDER.length}, 84px)`, gap: 0.5 }}>
            <Box />
            {RISK_ORDER.map((im) => (
              <Box key={im} sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{im}</Typography>
              </Box>
            ))}
            {RISK_ORDER.map((p, pi) => (
              <React.Fragment key={p}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{p}</Typography>
                </Box>
                {RISK_ORDER.map((im, ii) => (
                  <Box
                    key={im}
                    sx={{
                      height: 56,
                      borderRadius: 1.5,
                      display: 'grid',
                      placeItems: 'center',
                      backgroundColor: cellColor(pi, ii),
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>{matrix[pi][ii] || 0}</Typography>
                  </Box>
                ))}
              </React.Fragment>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Rows: probability · Columns: impact
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Risks</Typography>
        {risks.length === 0 ? (
          <TableEmptyState title="No risks logged" hint="Log a risk to start tracking it here." />
        ) : (
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Title</TableCell>
                    <TableCell sx={headerCellSx}>Probability</TableCell>
                    <TableCell sx={headerCellSx}>Impact</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Mitigation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {risks.map((r, i) => (
                    <TableRow key={r?.id ?? i} sx={bodyRowSx}>
                      <TableCell sx={{ fontWeight: 600 }}>{r?.title || '—'}</TableCell>
                      <TableCell><TypePill label={r?.probability || '—'} tint="#7c3aed" /></TableCell>
                      <TableCell><StatusDot label={r?.impact || '—'} tone={riskLevelTone(r?.impact)} /></TableCell>
                      <TableCell><StatusDot label={r?.status || '—'} tone={riskTone(r?.status)} /></TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>{r?.mitigation || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        )}
      </Box>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Issues tab
// ---------------------------------------------------------------------------

const IssuesTab: React.FC<{ issues: any[]; onAdd: () => void }> = ({ issues, onAdd }) => (
  <Box>
    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
      <Button size="small" startIcon={<Add />} onClick={onAdd}>Add Issue</Button>
    </Stack>
    {issues.length === 0 ? (
      <TableEmptyState title="No issues raised" hint="Raise an issue to start tracking it here." />
    ) : (
      <TableSurface>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Title</TableCell>
                <TableCell sx={headerCellSx}>Severity</TableCell>
                <TableCell sx={headerCellSx}>Owner</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.map((i, idx) => (
                <TableRow key={i?.id ?? idx} sx={bodyRowSx}>
                  <TableCell sx={{ fontWeight: 600 }}>{i?.title || '—'}</TableCell>
                  <TableCell><StatusDot label={i?.severity || '—'} tone={severityTone(i?.severity)} /></TableCell>
                  <TableCell>{i?.owner_name || i?.owner || '—'}</TableCell>
                  <TableCell><StatusDot label={i?.status || '—'} tone={issueTone(i?.status)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Change Requests tab
// ---------------------------------------------------------------------------

const ChangeRequestsTab: React.FC<{
  changeRequests: any[];
  onAdd: () => void;
  onStatus: (id: number, status: string) => void;
}> = ({ changeRequests, onAdd, onStatus }) => (
  <Box>
    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
      <Button size="small" startIcon={<Add />} onClick={onAdd}>Create Change Request</Button>
    </Stack>
    {changeRequests.length === 0 ? (
      <TableEmptyState title="No change requests" hint="Scope changes raised against this project will appear here." />
    ) : (
      <TableSurface>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Title</TableCell>
                <TableCell sx={headerCellSx} align="right">Cost Impact</TableCell>
                <TableCell sx={headerCellSx} align="right">Schedule Impact (days)</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changeRequests.map((cr, i) => {
                const status = String(cr?.status || '').toUpperCase();
                return (
                  <TableRow key={cr?.id ?? i} sx={bodyRowSx}>
                    <TableCell sx={{ fontWeight: 600 }}>{cr?.title || '—'}</TableCell>
                    <TableCell align="right" title={moneyTitle(cr?.cost_impact)}>{moneyCompact(cr?.cost_impact) ?? '—'}</TableCell>
                    <TableCell align="right">{cr?.schedule_impact_days ?? '—'}</TableCell>
                    <TableCell><StatusDot label={cr?.status || '—'} tone={crTone(cr?.status)} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {status === 'DRAFT' && cr?.id && (
                          <Button size="small" variant="outlined" onClick={() => onStatus(cr.id, 'SUBMITTED')}>Submit</Button>
                        )}
                        {['SUBMITTED', 'UNDER_REVIEW'].includes(status) && cr?.id && (
                          <>
                            <Button size="small" variant="outlined" color="success" onClick={() => onStatus(cr.id, 'APPROVED')}>Approve</Button>
                            <Button size="small" variant="outlined" color="error" onClick={() => onStatus(cr.id, 'REJECTED')}>Reject</Button>
                          </>
                        )}
                        {status === 'APPROVED' && cr?.id && (
                          <Button size="small" variant="outlined" onClick={() => onStatus(cr.id, 'IMPLEMENTED')}>Mark Implemented</Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Documents tab
// ---------------------------------------------------------------------------

const DocumentsTab: React.FC<{ documents: any[]; onAdd: () => void }> = ({ documents, onAdd }) => (
  <Box>
    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
      <Button size="small" startIcon={<Add />} onClick={onAdd}>Add Document Link</Button>
    </Stack>
    {documents.length === 0 ? (
      <TableEmptyState title="No documents linked" hint="Add a link to a spec, drawing, or contract document." />
    ) : (
      <Stack spacing={1}>
        {documents.map((d, i) => (
          <Paper
            key={d?.id ?? i}
            elevation={0}
            sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}
          >
            <Description sx={{ color: 'text.disabled' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} noWrap>{d?.file_name || d?.fileName || 'Untitled document'}</Typography>
              {d?.created_at && <Typography variant="caption" color="text.secondary">{dateText(d.created_at)}</Typography>}
            </Box>
            {d?.url && (
              <Tooltip title="Open document">
                <IconButton size="small" component="a" href={d.url} target="_blank" rel="noopener noreferrer">
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Paper>
        ))}
      </Stack>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Timeline tab
// ---------------------------------------------------------------------------

const kindIcon = (kind: any) => {
  const k = String(kind || '').toUpperCase();
  if (k.includes('COST')) return <AttachMoney sx={{ fontSize: 16 }} />;
  if (k.includes('MILESTONE')) return <Flag sx={{ fontSize: 16 }} />;
  if (k.includes('RISK')) return <Warning sx={{ fontSize: 16 }} />;
  if (k.includes('ISSUE')) return <ReportProblem sx={{ fontSize: 16 }} />;
  if (k.includes('CHANGE')) return <ChangeCircle sx={{ fontSize: 16 }} />;
  if (k.includes('BILL') || k.includes('INVOICE')) return <Receipt sx={{ fontSize: 16 }} />;
  if (k.includes('PAY')) return <Payments sx={{ fontSize: 16 }} />;
  if (k.includes('PO') || k.includes('PROCURE')) return <ShoppingCart sx={{ fontSize: 16 }} />;
  if (k.includes('TASK')) return <Engineering sx={{ fontSize: 16 }} />;
  if (k.includes('DOC')) return <Description sx={{ fontSize: 16 }} />;
  return <CalendarMonth sx={{ fontSize: 16 }} />;
};

const hashIndex = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

const TimelineTab: React.FC<{ items: any[]; ct: ReturnType<typeof useChartTokens>; navigate: (p: string) => void }> = ({
  items,
  ct,
  navigate,
}) => {
  if (items.length === 0) return <TableEmptyState title="No activity recorded yet" hint="Events across cost, billing, milestones and more will appear here as they happen." />;

  const sorted = [...items].sort((a, b) => {
    const ta = a?.ts ? new Date(a.ts).getTime() : 0;
    const tb = b?.ts ? new Date(b.ts).getTime() : 0;
    return tb - ta;
  });

  return (
    <Stack spacing={0}>
      {sorted.map((it, i) => {
        const tint = ct.series(hashIndex(String(it?.kind || 'event')) % 7);
        return (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, pb: i < sorted.length - 1 ? 2 : 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: `${tint}1a`,
                  color: tint,
                  flexShrink: 0,
                }}
              >
                {kindIcon(it?.kind)}
              </Box>
              {i < sorted.length - 1 && <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'divider', mt: 0.5, minHeight: 12 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pb: 0.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: '0.875rem', cursor: it?.link ? 'pointer' : 'default', color: it?.link ? 'primary.main' : 'text.primary' }}
                  onClick={() => it?.link && navigate(it.link)}
                >
                  {it?.label || 'Event'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {it?.ts ? dateText(it.ts) : '—'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                {it?.kind && <TypePill label={it.kind} tint={tint} />}
                {numOrNull(it?.amount) !== null && (
                  <Typography variant="caption" sx={{ fontWeight: 700 }} title={moneyTitle(it.amount)}>
                    {moneyCompact(it.amount)}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default ProjectWorkspacePage;
